// OrgSettingsAudit.js
//
// Build 25.1: dedicated full-audit-history page at
// /admin/org-settings/audit. The Organization Settings page shows the
// most recent 5 entries inline; this page shows up to 500 with field
// filtering.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFullAudit } from './services/orgSettingsService';
import './OrgSettings.css';

function OrgSettingsAudit({ user, userProfile }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    document.title = 'Audit Log - Wixon & Cross Desk Suite';
  }, []);

  useEffect(() => {
    if (userProfile && userProfile.role !== 'Admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [userProfile, navigate]);

  useEffect(() => {
    if (!user || userProfile?.role !== 'Admin') return;
    getFullAudit(500).then(rows => {
      setEntries(rows);
      setLoading(false);
    });
  }, [user, userProfile]);

  const filtered = filter === 'All'
    ? entries
    : entries.filter(e => e.field === filter);

  return (
    <div className="org-settings-page">
      <header className="org-settings-header">
        <div className="org-settings-header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>🖥️ Desktop</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>📦 Dashboard</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/admin')}>⚙️ Admin</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/admin/org-settings')}>🏢 Organization Settings</button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">📜 Audit Log</span>
          </div>
          <div className="org-settings-user-info">
            <span className="user-email">{user.email}</span>
            <span className="role-badge">Admin</span>
          </div>
        </div>
      </header>

      <main className="org-settings-main">
        <h1 className="org-settings-title">Audit Log</h1>
        <p className="org-settings-subtitle">
          Every change to organization settings, ordered most-recent first.
          Capped at the latest 500 entries.
        </p>

        <section className="org-settings-section">
          <div className="audit-header" style={{ marginBottom: '1rem' }}>
            <div>
              <label style={{ marginRight: '0.5rem', fontSize: '0.85rem', color: '#6b7280' }}>Filter:</label>
              <select
                value={filter}
                onChange={e => setFilter(e.target.value)}
                style={{
                  padding: '0.35rem 0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.85rem'
                }}
              >
                <option value="All">All changes</option>
                <option value="enabledAppIds">Enabled apps</option>
                <option value="orgName">Organization name</option>
              </select>
            </div>
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {loading ? (
            <p>Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="audit-empty">No changes recorded yet.</p>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Changed</th>
                  <th>Summary</th>
                  <th>Confirmed with</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(entry => (
                  <tr key={entry.id}>
                    <td className="audit-when">{formatTime(entry.at)}</td>
                    <td className="audit-who">{entry.userEmail}</td>
                    <td><span className="audit-field">{labelForField(entry.field)}</span></td>
                    <td className="audit-summary">{detailForEntry(entry)}</td>
                    <td style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {entry.confirmedWith || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}

function formatTime(ts) {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleString();
}

function labelForField(field) {
  if (field === 'enabledAppIds') return 'Enabled apps';
  if (field === 'orgName') return 'Org name';
  return field;
}

function detailForEntry(entry) {
  if (entry.field === 'orgName') {
    return `"${entry.before || ''}" → "${entry.after || ''}"`;
  }
  if (entry.field === 'enabledAppIds') {
    const before = entry.before || [];
    const after = entry.after || [];
    const added = after.filter(a => !before.includes(a));
    const removed = before.filter(a => !after.includes(a));
    const parts = [];
    if (added.length) parts.push(`+ ${added.join(', ')}`);
    if (removed.length) parts.push(`− ${removed.join(', ')}`);
    return parts.length ? parts.join(' ; ') : 'no net change';
  }
  return '';
}

export default OrgSettingsAudit;
